import { ArrowBackIcon, ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import {
  HStack,
  VStack,
  Link,
  Button,
  Text,
  Spinner,
  BoxProps,
  TextProps,
  Tag,
  Stack,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Tooltip,
} from "@chakra-ui/react";
import { KYDFullContact } from "common/interfaces/KYDCampaign";
import { useState, useEffect, useCallback } from "react";
import actions from "src/actions";
import { useParams, useNavigate } from "react-router-dom";
import StatCard from "../../Components/StatCard";
import { formatCurrency } from "src/Manager/EventDetail/Nav/Reporting/SettlementReportSection";
import dayjs from "dayjs";
import PurchaseHistoryTable from "./PurchaseHistoryTable";
import { genderToText } from "common/services/util/campaigns";
import { FiEdit } from "react-icons/fi";
import { useDialogWithOptions } from "src/Common/Dialog";
import { Link as RouterLink } from "react-router-dom";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";
import DeleteContactDialog, {
  DeleteContactDialogContext,
} from "../DeleteContactDialog";
import UnsubscribeContactDialog, {
  UnsubscribeContactDialogContext,
} from "./UnsubscribeContactDialog";
import { DialogProvider } from "src/Common/Dialog";
import AddFanDialog from "../AddFanDialog";

const InfoText = ({
  title,
  description,
  ...props
}: {
  title: string;
  description?: string;
} & TextProps) => {
  return (
    <Text
      color="#222"
      fontFamily="Work Sans"
      fontSize="16px"
      fontStyle="normal"
      fontWeight="400"
      lineHeight="140%"
      {...props}
    >
      {title}
      {description && (
        <Text
          as="span"
          color="rgba(34, 34, 34, 0.60)"
          fontSize="16px"
          fontWeight="400"
          lineHeight="140%"
        >
          {" "}
          {description}
        </Text>
      )}
    </Text>
  );
};

const Card = ({
  title,
  children,
  ...props
}: {
  title: string;
  children: React.ReactNode;
} & BoxProps) => {
  return (
    <VStack
      alignSelf="stretch"
      alignItems="flex-start"
      w="100%"
      minHeight="154px"
      borderRadius="10px"
      border="1px solid #E6E6E6"
      background="#FFFFFF"
      boxShadow="0px 10px 15px -5px rgba(247, 247, 247, 0.90)"
      px="16px"
      py="12px"
      h="186px"
      {...props}
    >
      <Text
        color="rgba(26, 32, 44, 0.75)"
        fontSize="16px"
        fontWeight="500"
        lineHeight="150%"
        letterSpacing="-0.48px"
        mb="6px"
      >
        {title}
      </Text>
      {children}
    </VStack>
  );
};

const ContactDetail = ({ permissions = [] }: { permissions?: KYDScope[] }) => {
  const [contact, setContact] = useState<KYDFullContact | null>();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const deleteDialog = useDialogWithOptions(DeleteContactDialogContext);
  const unsubscribeDialog = useDialogWithOptions(
    UnsubscribeContactDialogContext
  );

  const updateContact = useCallback(
    async (data: Partial<KYDFullContact>) => {
      try {
        await actions.ADMIN.updateContact(data);
      } catch (error) {
        console.error("Error updating contact:", error);
        throw error;
      }
    },
    [setContact]
  );

  const fetchContact = useCallback(async () => {
    setLoading(true);
    try {
      const contact = await actions.ADMIN.fetchContact(id);
      setContact(contact);
    } catch (error: any) {
      console.error("Failed to fetch contact:", error);
      toast({
        title: "Contact not found",
        description: "This contact may have been deleted.",
        status: "error",
      });
      navigate("/campaigns/contacts");
    } finally {
      setLoading(false);
    }
  }, [id, toast, navigate]);

  const openAddTagDialog = useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  const handleUnsubscribe = useCallback(async () => {
    setActionLoading(true);
    try {
      await actions.ADMIN.unsubscribeContact(id);
      toast({
        title: "Contact unsubscribed",
        description: "Contact has been unsubscribed from emails",
        status: "success",
      });
      fetchContact();
    } catch (error) {
      console.error("Failed to unsubscribe contact:", error);
      toast({
        title: "Failed to unsubscribe contact",
        description: "Please try again",
        status: "error",
      });
    } finally {
      setActionLoading(false);
      unsubscribeDialog.close();
    }
  }, [id, toast, fetchContact, unsubscribeDialog]);

  const handleDelete = useCallback(async () => {
    setActionLoading(true);
    try {
      await actions.ADMIN.deleteContact(id);
      toast({
        title: "Contact deleted",
        description: "Contact deleted successfully",
        status: "success",
      });
      navigate("/campaigns/contacts");
    } catch (error) {
      console.error("Failed to delete contact:", error);
      toast({
        title: "Failed to delete contact",
        description: "Please try again",
        status: "error",
      });
      setActionLoading(false);
      deleteDialog.close();
    }
  }, [id, toast, navigate, deleteDialog]);

  const openUnsubscribeDialog = useCallback(() => {
    unsubscribeDialog.open({
      title: "Unsubscribe Contact",
      actions: [
        {
          type: "outline",
          text: "Cancel",
          onClick: () => unsubscribeDialog.close(),
          buttonProps: { variant: "outline" },
        },
        {
          type: "solid",
          text: "Unsubscribe",
          onClick: handleUnsubscribe,
          buttonProps: {
            bg: "#9B2C2C",
            color: "white",
            _hover: { bg: "#822727" },
          },
        },
      ],
    });
  }, [unsubscribeDialog, handleUnsubscribe]);

  const openDeleteDialog = useCallback(() => {
    deleteDialog.open({
      title: "Delete Contact",
      actions: [
        {
          type: "outline",
          text: "Cancel",
          onClick: () => deleteDialog.close(),
          buttonProps: { variant: "outline" },
        },
        {
          type: "solid",
          text: "Delete Contact",
          onClick: handleDelete,
          buttonProps: {
            bg: "#9B2C2C",
            color: "white",
            _hover: { bg: "#822727" },
          },
        },
      ],
    });
  }, [deleteDialog, handleDelete]);

  useEffect(() => {
    fetchContact();
  }, [id]);

  if (loading || !contact) return <Spinner />;

  const subscriptionStatusText =
    contact.status === "PENDING_CONFIRMATION"
      ? "Pending Confirmation"
      : contact.status === "DISABLED" || contact.unsubscribed
      ? "Unsubscribed"
      : "Subscribed";

  const subscriptionStatusBg =
    contact.status === "PENDING_CONFIRMATION"
      ? "#FEF3C7"
      : contact.status === "DISABLED" || contact.unsubscribed
      ? "gray.100"
      : "#D1FAE5";

  const subscriptionStatusColor =
    contact.status === "PENDING_CONFIRMATION"
      ? "#92400E"
      : contact.status === "DISABLED" || contact.unsubscribed
      ? "gray.600"
      : "#065F46";

  return (
    <VStack gap="24px" py={4}>
      <ShowIfHasScope
        permissions={permissions}
        checkFor={["crm/contacts:write", "crm/write"]}
      >
        <>
          <AddFanDialog
            isOpen={isEditModalOpen}
            contact={contact}
            onUpdate={fetchContact}
            onClose={() => setIsEditModalOpen(false)}
          />
          <UnsubscribeContactDialog
            onClose={() => unsubscribeDialog.close()}
            isLoading={actionLoading}
          />
          <DeleteContactDialog
            onClose={() => deleteDialog.close()}
            isLoading={actionLoading}
          />
        </>
      </ShowIfHasScope>
      <HStack w="100%" h="100%" justifyContent="space-between">
        <VStack alignItems="flex-start">
          <Link as={RouterLink} to="/campaigns/contacts">
            <Button
              variant="link"
              color="#888888"
              fontWeight="500"
              fontSize="14px"
              leftIcon={<ArrowBackIcon />}
            >
              Contacts
            </Button>
          </Link>
          <HStack spacing={4}>
            <Text
              fontSize="24px"
              color="#1A202C"
              fontWeight="500"
              overflow="hidden"
              textOverflow="ellipsis"
              lineHeight="30px"
              letterSpacing="-0.72px"
            >
              {contact.first_name} {contact.last_name}
            </Text>
            {contact.status === "DISABLED" && (
              <Tag
                size="md"
                bg="gray.100"
                color="gray.600"
                borderRadius="md"
                textTransform="uppercase"
                fontWeight="600"
                fontSize="xs"
                px={3}
                py={1}
              >
                Disabled
              </Tag>
            )}
          </HStack>
          <Text fontSize="small" color="gray.500">
            Created on{" "}
            {contact.created_at
              ? dayjs(contact.created_at).format("MMM D, YYYY")
              : "N/A"}
          </Text>
        </VStack>
        <ShowIfHasScope
          permissions={permissions}
          checkFor={["crm/contacts:write", "crm/write"]}
        >
          <Menu isLazy placement="bottom-end">
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              borderRadius="full"
              bg="black"
              textColor="white"
              _hover={{ bg: "gray.800" }}
              fontSize="16px"
              isLoading={actionLoading}
            >
              Actions
            </MenuButton>
            <MenuList zIndex={10}>
              {contact.status !== "DISABLED" && (
                <MenuItem onClick={() => setIsEditModalOpen(true)}>
                  Edit contact info
                </MenuItem>
              )}
              {!contact.unsubscribed && (
                <>
                  {contact.status !== "DISABLED" && <MenuDivider />}
                  <MenuItem onClick={openUnsubscribeDialog}>
                    Unsubscribe from email
                  </MenuItem>
                </>
              )}
              <MenuDivider />
              <MenuItem
                onClick={openDeleteDialog}
                color="gray.500"
                icon={<CloseIcon boxSize={3} />}
              >
                Delete contact
              </MenuItem>
            </MenuList>
          </Menu>
        </ShowIfHasScope>
      </HStack>
      <Stack
        direction={{ base: "column", md: "row" }}
        w="100%"
        h="100%"
        justifyContent="space-between"
        flexWrap={"wrap"}
        gap={"24px"}
        mt="16px"
      >
        <StatCard
          flex={1}
          title="Total Spent"
          value={formatCurrency(
            Math.round(contact.total_spent.amount),
            contact.total_spent.currency as Dinero.Currency
          )}
        />
        <StatCard
          flex={1}
          title="Avg. Order Value"
          value={formatCurrency(
            Math.round(contact.average_order_value.amount),
            contact.average_order_value.currency as Dinero.Currency
          )}
        />
        <StatCard
          flex={1}
          title="Avg. Tickets Per Order"
          value={contact.average_items_per_order.toFixed(1)}
        />
        <StatCard
          flex={1}
          title="Last Purchase"
          value={
            contact.purchase_history.length > 0
              ? dayjs(contact.purchase_history[0].date).fromNow()
              : "N/A"
          }
        />
      </Stack>
      <Stack w="100%" gap="24px" direction={{ base: "column", md: "row" }}>
        <Card title="Info">
          {contact.email && <InfoText title={`Email: ${contact.email}`} />}
          {contact.phone_number && (
            <InfoText title={`Phone: ${contact.phone_number}`} />
          )}
          {contact.gender && (
            <InfoText
              title={`Gender: ${
                contact.gender ? genderToText(contact.gender) : ""
              }`}
            />
          )}
          {contact.dob && (
            <InfoText
              title={`Age: ${dayjs().diff(dayjs(contact.dob), "year", false)}`}
            />
          )}
        </Card>
        <Card title="Campaign Engagement">
          <HStack>
            {(contact.status === "DISABLED" || contact.unsubscribed) &&
            contact.unsubscribed_at ? (
              <Tooltip
                label={`Unsubscribed on ${dayjs(contact.unsubscribed_at).format(
                  "MMM D, YYYY [at] h:mm A"
                )}`}
                hasArrow
              >
                <Tag
                  size="md"
                  bg={subscriptionStatusBg}
                  color={subscriptionStatusColor}
                  borderRadius="md"
                  textTransform="uppercase"
                  fontWeight="600"
                  fontSize="xs"
                  px={3}
                  py={1}
                >
                  {subscriptionStatusText}
                </Tag>
              </Tooltip>
            ) : (
              <Tag
                size="md"
                bg={subscriptionStatusBg}
                color={subscriptionStatusColor}
                borderRadius="md"
                textTransform="uppercase"
                fontWeight="600"
                fontSize="xs"
                px={3}
                py={1}
              >
                {subscriptionStatusText}
              </Tag>
            )}
          </HStack>
          <InfoText
            title={`${contact.total_emails_received || 0} emails received`}
          />
          <InfoText
            title={`${contact.total_emails_opened || 0} emails opened`}
          />
          <InfoText
            title={`${contact.total_emails_clicked || 0} emails clicked`}
          />
        </Card>
        <Card title="Tags">
          <HStack flexWrap={"wrap"} gap={"8px"}>
            {contact.tags.map((tag) => (
              <Tag
                key={tag}
                sx={{
                  flexShrink: 0,
                  color: "rgba(34, 34, 34, 0.80)",
                  fontFamily: "Work Sans",
                  fontSize: "14px",
                }}
              >
                {tag}
              </Tag>
            ))}
            <ShowIfHasScope
              permissions={permissions}
              checkFor={["crm/contacts:write", "crm/write"]}
            >
              {contact.status !== "DISABLED" && (
                <Tag
                  onClick={openAddTagDialog}
                  sx={{
                    cursor: "pointer",
                    color: "rgba(34, 34, 34, 0.80)",
                    fontFamily: "Work Sans",
                    fontSize: "14px",
                  }}
                >
                  <HStack gap={"4px"}>
                    <FiEdit />
                    <Text>Edit Tags</Text>
                  </HStack>
                </Tag>
              )}
            </ShowIfHasScope>
          </HStack>
        </Card>
      </Stack>
      <VStack alignItems={"flex-start"} w={"100%"} mt="24px">
        <Text fontSize="24px" fontWeight="600" mb={"12px"}>
          Purchase History
        </Text>
        {contact.purchase_history?.length > 0 ? (
          <PurchaseHistoryTable data={contact.purchase_history || []} />
        ) : (
          <Text>No purchase history</Text>
        )}
      </VStack>
    </VStack>
  );
};

const ContactDetailWithDialogs = ({
  permissions,
}: {
  permissions?: KYDScope[];
}) => (
  <DialogProvider context={DeleteContactDialogContext}>
    <DialogProvider context={UnsubscribeContactDialogContext}>
      <ContactDetail permissions={permissions} />
    </DialogProvider>
  </DialogProvider>
);

export default ContactDetailWithDialogs;
